import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { validateAccessToken, ValidateAccessResponse } from '../services/api';

interface AccessControlContextType {
  token: string | null;
  isValid: boolean;
  isExpired: boolean;
  remainingTime: number;
  accessCount: number;
  isLoading: boolean;
  error: string | null;
  setToken: (token: string | null) => void;
  clearToken: () => void;
  refreshToken: () => Promise<void>;
}

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

interface AccessControlProviderProps {
  children: ReactNode;
}

export const AccessControlProvider: React.FC<AccessControlProviderProps> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(() => {
    // First check URL for token (highest priority), then localStorage
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        return tokenFromUrl;
      }
    } catch {
      // Ignore URL parsing errors
    }
    
    // Fallback to localStorage
    try {
      return localStorage.getItem('access_token');
    } catch {
      return null;
    }
  });
  const [isValid, setIsValid] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [accessCount, setAccessCount] = useState(0);
  const [isLoading, setIsLoading] = useState(() => {
    // Set loading to true if token is in URL, as we need to validate it first
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('token');
    } catch {
      return false;
    }
  });
  const [error, setError] = useState<string | null>(null);

  // Use refs to prevent infinite loops and track validation state
  const isValidatingRef = useRef(false);
  const lastValidatedTokenRef = useRef<string | null>(null);
  const hasProcessedUrlTokenRef = useRef(false);
  const lastRemainingTimeThresholdRef = useRef<number>(-1); // Track last threshold we checked

  // Check if current path is an admin route (should skip token validation)
  const isAdminRoute = useCallback(() => {
    const path = window.location.pathname;
    const isAdminPath = path.includes('/7549516669/211fa01046/ID2025009');
    const isDemoPath = path === '/demo' || path.startsWith('/demo/');
    // return path.includes('/7549516669/211fa01046/ID2025009');
    return isAdminPath || isDemoPath;
  }, []);

  // Validate token function - centralized to avoid duplication
  const validateTokenInternal = useCallback(async (tokenToValidate: string, removeFromUrl: boolean = false) => {
    // Prevent duplicate validations
    if (isValidatingRef.current && lastValidatedTokenRef.current === tokenToValidate) {
      console.log('⏸️ Skipping duplicate validation for same token');
      return;
    }

    isValidatingRef.current = true;
    lastValidatedTokenRef.current = tokenToValidate;
    setIsLoading(true);
    setError(null);

    try {
      console.log('🔍 Validating token with backend...');
      const response: ValidateAccessResponse = await validateAccessToken(tokenToValidate);
      console.log('✅ Token validation response:', response);
      
      setIsValid(response.isValid);
      // If remainingTime is 0 or negative, token is expired regardless of isExpired flag
      const actuallyExpired = response.isExpired || response.remainingTime <= 0;
      setIsExpired(actuallyExpired);
      setRemainingTime(Math.max(0, response.remainingTime)); // Ensure non-negative
      setAccessCount(response.accessCount);

      if (response.isValid && !actuallyExpired) {
        // Token is valid - remove from URL if needed
        if (removeFromUrl) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          window.history.replaceState({}, '', newUrl.toString());
          console.log('🧹 Token removed from URL after successful validation');
        }
      } else {
        if (response.isExpired) {
          setError('Access token has expired');
        } else {
          setError(response.message || 'Invalid access token');
        }
      }
    } catch (err: any) {
      console.error('❌ Backend token validation failed:', err);
      
      // Check if it's a rate limit error (429)
      if (err.response?.status === 429) {
        console.warn('⚠️ Rate limit hit, using fallback JWT validation');
        // Don't show error on rate limit - use fallback instead
        setError(null);
      } else {
        setError(err.message || 'Failed to validate token. Please check your connection.');
      }

      // Fallback: Try to parse token locally as JWT
      try {
        const parts = tokenToValidate.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const now = Math.floor(Date.now() / 1000);
          
          const remainingSecs = payload.exp - now;
          if (payload.exp && remainingSecs > 0) {
            console.log('🔄 Using fallback JWT validation - token appears valid');
            setIsValid(true);
            // Ensure expiration is handled correctly
            const actuallyExpired = remainingSecs <= 0;
            setIsExpired(actuallyExpired);
            setRemainingTime(Math.max(0, remainingSecs));
            setAccessCount(0);
            
            if (removeFromUrl) {
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete('token');
              window.history.replaceState({}, '', newUrl.toString());
              console.log('🧹 Token removed from URL after fallback validation');
            }
            
            // Clear error if fallback succeeds
            if (err.response?.status === 429) {
              setError(null);
            }
          } else {
            console.log('❌ Fallback validation failed - token expired');
            setError('Access token has expired');
            setIsValid(false);
            setIsExpired(true);
          }
        } else {
          throw new Error('Invalid token format');
        }
      } catch (fallbackErr: any) {
        console.error('❌ Fallback validation also failed:', fallbackErr);
        // Only set error if we don't have a rate limit
        if (err.response?.status !== 429) {
          setIsValid(false);
          setIsExpired(false);
        }
      }
    } finally {
      setIsLoading(false);
      isValidatingRef.current = false;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    if (!token) return;
    
    // Prevent duplicate refresh calls
    if (isValidatingRef.current && lastValidatedTokenRef.current === token) {
      console.log('⏸️ Skipping duplicate refresh for same token');
      return;
    }

    await validateTokenInternal(token, false);
  }, [token, validateTokenInternal]);

  // Extract and validate token from URL on mount (only once)
  useEffect(() => {
    // Skip validation entirely for admin routes
    if (isAdminRoute()) {
      console.log('🔒 Admin route detected - skipping token validation');
      hasProcessedUrlTokenRef.current = true;
      return;
    }

    // Only process URL token once on mount
    if (hasProcessedUrlTokenRef.current) {
      return;
    }

    const extractTokenFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      
      // Get current token from localStorage (not from state to avoid dependency)
      let currentToken = null;
      try {
        currentToken = localStorage.getItem('access_token');
      } catch (e) {
        // Ignore
      }
      
      if (tokenFromUrl) {
        console.log('🔑 Token found in URL on mount:', tokenFromUrl.substring(0, 20) + '...');
        
        // CRITICAL: Save token to localStorage FIRST, before any validation
        try {
          localStorage.setItem('access_token', tokenFromUrl);
          console.log('💾 Token saved to localStorage immediately');
        } catch (e) {
          console.warn('Failed to save token to localStorage:', e);
        }
        
        // Set token state (this will trigger if different from current)
        if (tokenFromUrl !== currentToken) {
          setTokenState(tokenFromUrl);
        }
        
        // Mark as processed
        hasProcessedUrlTokenRef.current = true;
        
        // Validate token and remove from URL
        await validateTokenInternal(tokenFromUrl, true);
      } else {
        hasProcessedUrlTokenRef.current = true;
        
        // No token in URL - validate existing token if any
        if (currentToken) {
          console.log('🔍 No token in URL, validating existing token from localStorage...');
          setTokenState(currentToken);
          await validateTokenInternal(currentToken, false);
        }
      }
    };

    extractTokenFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run once on mount to extract URL token

  // Validate token when it changes (but only if we've already processed URL token)
  useEffect(() => {
    // Skip validation for admin routes
    if (isAdminRoute()) {
      return;
    }

    // Only validate if we've processed URL token and token changed
    if (!hasProcessedUrlTokenRef.current) {
      return;
    }

    if (token && !isLoading && !isValidatingRef.current) {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      
      // Don't validate if token is in URL (handled by mount effect)
      if (!tokenFromUrl && lastValidatedTokenRef.current !== token) {
        console.log('🔍 Token changed, validating...');
        validateTokenInternal(token, false);
      }
    } else if (!token) {
      setIsValid(false);
      setIsExpired(false);
      setRemainingTime(0);
      setAccessCount(0);
      setError(null);
      lastValidatedTokenRef.current = null;
    }
  }, [token, isLoading, validateTokenInternal, isAdminRoute]);

  // Auto-refresh token only when TTL is about to expire (use threshold-based checking to avoid constant re-runs)
  // Calculate threshold bucket: only re-run effect when crossing 30-second boundaries
  const remainingTimeThreshold = Math.floor(remainingTime / 30);
  
  useEffect(() => {
    // Skip validation for admin routes
    if (isAdminRoute()) {
      return;
    }

    if (!token || !isValid || isExpired || remainingTime <= 0) {
      lastRemainingTimeThresholdRef.current = -1;
      return;
    }

    // Use threshold-based approach: only re-run effect when crossing thresholds (not every second)
    // Thresholds: >60s (don't validate), 60s-30s (check soon), <=30s (validate now)
    const threshold1 = 60; // First threshold: 1 minute
    const threshold2 = 30; // Second threshold: 30 seconds
    
    // Determine which threshold bucket we're in
    let currentThreshold = -1;
    if (remainingTime > threshold1) {
      currentThreshold = threshold1;
    } else if (remainingTime > threshold2) {
      currentThreshold = threshold2;
    } else {
      currentThreshold = 0; // Less than 30s - validate immediately
    }

    // If we're still in the same threshold bucket, don't set up new timers
    // This prevents re-running every second when remainingTime changes within the same threshold
    if (lastRemainingTimeThresholdRef.current === currentThreshold && currentThreshold > 0) {
      return;
    }

    // Update threshold tracking
    lastRemainingTimeThresholdRef.current = currentThreshold;

    // Only set up timers when crossing into a new threshold
    // If remainingTime > 60s: schedule validation for 30s before expiry
    // If remainingTime 60s-30s: schedule validation for 30s before expiry  
    // If remainingTime <= 30s: validate immediately
    
    let timer: NodeJS.Timeout | null = null;
    
    if (remainingTime > threshold2) {
      // More than 30 seconds: schedule validation for 30 seconds before expiry
      const timeUntilValidate = Math.max(1000, (remainingTime - threshold2) * 1000);
      console.log(`⏸️ Token valid for ${Math.floor(remainingTime / 60)}m ${remainingTime % 60}s. Will validate in ${Math.floor(timeUntilValidate / 1000)}s`);
      timer = setTimeout(() => {
        if (!isValidatingRef.current) {
          console.log(`🔄 Token expiring soon (approximately 30s remaining), validating...`);
          refreshToken();
        }
      }, timeUntilValidate);
    } else {
      // Less than 30 seconds: validate immediately (only once)
      if (!isValidatingRef.current && lastValidatedTokenRef.current !== token) {
        console.log(`🔄 Token expiring very soon (${remainingTime}s remaining), validating immediately...`);
        refreshToken();
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
    // Only re-run when crossing 30-second boundaries, not every second
    // We use remainingTimeThreshold instead of remainingTime to avoid constant re-runs
    // remainingTime is used inside the effect but intentionally not in deps to prevent validation loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isValid, isExpired, remainingTimeThreshold, refreshToken, isAdminRoute]); // remainingTime intentionally excluded - using threshold-based approach

  // Ref to track if countdown is running and the initial remainingTime when countdown started
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownStartTimeRef = useRef<number>(-1);

  // Monitor remainingTime and auto-expire when it reaches 0
  useEffect(() => {
    // Skip for admin routes
    if (isAdminRoute()) {
      return;
    }

    if (!token || isExpired) {
      // Clean up interval if token removed or expired
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        countdownStartTimeRef.current = -1;
      }
      // If remainingTime is 0 or less with a token, mark as expired
      if (remainingTime <= 0 && token) {
        setIsExpired(true);
        setIsValid(false);
      }
      return;
    }

    // Only restart countdown if remainingTime changed significantly (likely from validation)
    // or if countdown hasn't started yet
    const shouldRestartCountdown = 
      countdownStartTimeRef.current === -1 || 
      Math.abs(countdownStartTimeRef.current - remainingTime) > 5; // More than 5 seconds difference

    if (shouldRestartCountdown && remainingTime > 0) {
      // Clean up existing interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      // Start new countdown
      countdownStartTimeRef.current = remainingTime;
      
      countdownIntervalRef.current = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime <= 0) {
            // Time has expired
            setIsExpired(true);
            setIsValid(false);
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
              countdownStartTimeRef.current = -1;
            }
          }
          return newTime;
        });
      }, 1000); // Update every second
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        countdownStartTimeRef.current = -1;
      }
    };
  }, [token, isExpired, remainingTime, isAdminRoute]); // Include remainingTime to restart countdown when updated from validation

  // Auto-redirect when token expires
  useEffect(() => {
    const shouldRedirect =
      !isAdminRoute() && (isExpired || (token && remainingTime <= 0));
    // if (isExpired || (token && remainingTime <= 0 && !isAdminRoute())) {
    if (shouldRedirect) {
      const timer = setTimeout(() => {
        window.location.href = '/access-expired';
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
  }, [isExpired, remainingTime, token, isAdminRoute]);

  const setToken = (newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) {
      try {
        localStorage.setItem('access_token', newToken);
      } catch (e) {
        console.warn('Failed to save token to localStorage:', e);
      }
    } else {
      try {
        localStorage.removeItem('access_token');
      } catch (e) {
        console.warn('Failed to remove token from localStorage:', e);
      }
    }
    // Reset validation tracking when token changes
    lastValidatedTokenRef.current = null;
  };

  const clearToken = () => {
    setTokenState(null);
    setIsValid(false);
    setIsExpired(false);
    setRemainingTime(0);
    setAccessCount(0);
    setError(null);
    lastValidatedTokenRef.current = null;
    try {
      localStorage.removeItem('access_token');
    } catch (e) {
      console.warn('Failed to remove token from localStorage:', e);
    }
  };

  const value: AccessControlContextType = {
    token,
    isValid,
    isExpired,
    remainingTime,
    accessCount,
    isLoading,
    error,
    setToken,
    clearToken,
    refreshToken
  };

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};

export const useAccessControl = (): AccessControlContextType => {
  const context = useContext(AccessControlContext);
  if (context === undefined) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
};
